import React from 'react';

// Key used to guard the one-time auto-reload so a genuinely-missing chunk
// cannot trigger an infinite reload loop. Cleared on a successful render so a
// future deploy can self-heal an already-open tab again.
const RELOAD_FLAG = 'chunk-reload-once';

// A ChunkLoadError happens when the cached index.html points at content-hashed
// chunk filenames that a newer deploy no longer ships. Detect it by name or the
// bundler's message so we can recover instead of crashing.
function isChunkLoadError(error) {
  if (!error) return false;

  return error.name === 'ChunkLoadError' || /Loading chunk .* failed/i.test(error.message || '');
}

// sessionStorage can throw (private mode, disabled storage); never let the
// safety net itself become the crash.
function safeGetFlag() {
  try {
    return sessionStorage.getItem(RELOAD_FLAG);
  } catch (e) {
    return null;
  }
}

function safeSetFlag() {
  try {
    sessionStorage.setItem(RELOAD_FLAG, '1');
  } catch (e) {
    // ignore
  }
}

function safeClearFlag() {
  try {
    sessionStorage.removeItem(RELOAD_FLAG);
  } catch (e) {
    // ignore
  }
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, isChunkError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, isChunkError: isChunkLoadError(error) };
  }

  componentDidMount() {
    // Reaching a healthy mount means the current shell + chunks match, so
    // re-arm the guard for the next deploy.
    if (!this.state.hasError) {
      safeClearFlag();
    }
  }

  componentDidCatch(error) {
    // On the first chunk failure, reload once: the fresh index.html will
    // reference the new chunk hashes and the retry succeeds. The guard stops a
    // reload loop if the chunk is genuinely gone.
    if (isChunkLoadError(error) && !safeGetFlag()) {
      safeSetFlag();
      window.location.reload();
    }
  }

  handleReload = () => {
    safeClearFlag();
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // A first-time chunk error is about to auto-reload in componentDidCatch —
      // render nothing to avoid a flash of the fallback before the reload.
      if (this.state.isChunkError && !safeGetFlag()) {
        return null;
      }

      // Non-chunk error, or a chunk that stayed missing after the one reload:
      // show a friendly fallback with a manual reload instead of a white screen.
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: 24,
            textAlign: 'center',
            fontFamily: 'sans-serif',
            color: '#374151',
          }}
        >
          <h2 style={{ marginBottom: 8 }}>Đã có lỗi xảy ra</h2>
          <p style={{ marginBottom: 16, color: '#6b7280' }}>
            Vui lòng tải lại trang. Nếu lỗi vẫn tiếp tục, hãy liên hệ hỗ trợ.
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            style={{
              padding: '8px 20px',
              fontSize: 14,
              color: '#fff',
              background: '#2563eb',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
            }}
          >
            Tải lại trang
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
